import { CAST_AI } from '@cast/constants';
import {
  Icons,
  ListItem,
  ListItemText,
  Select,
  Box,
  Stack,
} from '@cast/design-system';
import { useRegionsQuery } from '@cast/shared-inventory-data-access';

import {
  InlineWithDescription,
  ControlWithDescription,
} from 'components/form-layouts';

import { CUSTOM_EDGE_PROVIDER } from '../../constants';
import { EnhancedEdgeLocation } from '../../types';
import { getRegionDisplayName } from '../../utils';

import { CspIcon } from '@cast/shared-csp-icons-ui';

type EdgeLocationSelectorProps = {
  edgeLocation: EnhancedEdgeLocation | undefined;
  eligibleEdgeLocations: EnhancedEdgeLocation[];
  onChange: (edgeLocation: EnhancedEdgeLocation | null) => void;
  readOnly?: boolean;
};

export const EdgeLocationSelector = ({
  edgeLocation,
  eligibleEdgeLocations,
  onChange,
  readOnly,
}: EdgeLocationSelectorProps) => {
  const { regions } = useRegionsQuery();

  const handleChange = (
    _option: EnhancedEdgeLocation,
    value: EnhancedEdgeLocation | null
  ) => {
    if (!value) {
      return;
    }

    onChange(value);
  };

  return (
    <Box sx={{ px: 32 }}>
      <InlineWithDescription
        description={
          <ControlWithDescription label="Edge location">
            {readOnly
              ? 'Edge location that has been selected'
              : `Select an edge location where ${CAST_AI} should provision this node.`}
          </ControlWithDescription>
        }
        controls={
          <Stack mb={16}>
            <Select
              testId="edge-location-select"
              disabled={readOnly}
              inputProps={{
                placeholder: 'Select edge location',
              }}
              options={eligibleEdgeLocations}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              optionLabel="name"
              value={edgeLocation ?? null}
              onChange={handleChange}
              renderOption={(option: EnhancedEdgeLocation, itemProps) => {
                const region =
                  option.provider === CUSTOM_EDGE_PROVIDER
                    ? 'Custom'
                    : getRegionDisplayName(option.region, regions);
                return (
                  <ListItem
                    {...itemProps}
                    startAdornment={
                      option.provider === CUSTOM_EDGE_PROVIDER ? (
                        <Icons.GearSix size={24} />
                      ) : (
                        <CspIcon csp={option.provider} size={24} />
                      )
                    }
                  >
                    <ListItemText
                      primary={option.name}
                      sx={{ py: 10 }}
                      primaryTypographyProps={{ typography: 'P12M' }}
                      secondary={region}
                      secondaryTypographyProps={{ typography: 'P10R' }}
                    />
                  </ListItem>
                );
              }}
            />
          </Stack>
        }
      />
    </Box>
  );
};
